// src/pages/LavorazioniTerminate.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { caricaReportCompleto } from '../utils/reportCompleto';

const API_BASE = '/api';

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatUserLabel = (u) => {
  if (!u) return '';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  if (full) return full;
  return u.username || '';
};

const textMatch = (value, filterValue) => {
  if (!filterValue) return true;
  return (value || '')
    .toString()
    .toLowerCase()
    .includes(filterValue.toLowerCase());
};

const inDateRange = (valueIso, fromStr, toStr) => {
  if (!fromStr && !toStr) return true;
  if (!valueIso) return false;

  const v = new Date(valueIso);
  if (Number.isNaN(v.getTime())) return false;
  const valueTime = v.setHours(0, 0, 0, 0);

  if (fromStr) {
    const f = new Date(fromStr);
    if (Number.isNaN(f.getTime())) return false;
    if (valueTime < f.setHours(0, 0, 0, 0)) return false;
  }

  if (toStr) {
    const t = new Date(toStr);
    if (Number.isNaN(t.getTime())) return false;
    if (valueTime > t.setHours(23, 59, 59, 999)) return false;
  }

  return true;
};

// estrae il "codice base": I25-00000 da I25-00000-2
const getBaseInternalCode = (fullCode) => {
  if (!fullCode) return 'SENZA-CODICE';
  const parts = fullCode.split('-');
  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1];
    if (/^\d+$/.test(lastPart)) {
      return parts.slice(0, parts.length - 1).join('-');
    }
  }
  return fullCode;
};

const LavorazioniTerminate = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]); // WorkInProgress chiusi

  const [viewType, setViewType] = useState('vit'); // 'vit' | 'manutenzione'
  const [expanded, setExpanded] = useState([]); // lista di baseCode espansi

  const [filters, setFilters] = useState({
    codiceEsterno: '',
    codiceInterno: '',
    tipoLavorazione: '',
    cliente: '',
    luogo: '',
    assegnatoA: '',
    dataRicezioneDa: '',
    dataRicezioneA: '',
    dataEseguitaDa: '',
    dataEseguitaA: '',
    statoOrdine: '', // 🔹 nuovo filtro
  });

  // ruolo utente per bottoni Elimina
  const [myRole] = useState(localStorage.getItem('role') || null);
  const isAdmin = myRole === 'admin';

  // stato modale eliminazione
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind: 'order'|'wip', id, displayCode }
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();

  // Carica dal backend tutte le lavorazioni terminate
  const loadTerminated = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/terminated`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero delle lavorazioni terminate.'
        );
      }
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore loadTerminated:', err);
      setError(err.message || 'Errore nella richiesta al server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTerminated();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const toggleExpanded = (baseCode) => {
    setExpanded((prev) =>
      prev.includes(baseCode)
        ? prev.filter((c) => c !== baseCode)
        : [...prev, baseCode]
    );
  };

  // Riapre la lavorazione (status -> in_corso)
  const handleReopen = async (wipId) => {
    setError('');
    setActionLoadingId(wipId);
    try {
      const res = await authFetch(`${API_BASE}/work/in-progress/${wipId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'in_corso' }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nella riapertura della lavorazione.'
        );
      }

      setItems((prev) => prev.filter((item) => item._id !== wipId));
    } catch (err) {
      console.error('Errore handleReopen:', err);
      setError(err.message || 'Errore nella richiesta al server.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Apre il report per modifica
  const handleOpenReport = async (item) => {
    const order = item.workOrder || {};

    // l'elenco e' leggero: il report completo si chiede solo ora
    const completo = await caricaReportCompleto(item);

    if (order.type === 'manutenzione') {
      navigate('/lavorazioni-in-corso/manutenzioni', {
        state: { fromWorkInProgress: completo },
      });
    } else {
      navigate('/lavorazioni-in-corso/vit', {
        state: { fromWorkInProgress: completo },
      });
    }
  };

  // ===============================
  // FILTRI
  // ===============================
  const filteredItems = useMemo(() => {
    return (items || []).filter((item) => {
      const order = item.workOrder || {};
      const summary = item.summary || {};

      const isManutenzione = order.type === 'manutenzione';
      if (viewType === 'vit' && isManutenzione) return false;
      if (viewType === 'manutenzione' && !isManutenzione) return false;

      const fullCode =
        summary.codiceInterno ||
        order.internalCode ||
        order.codiceInterno ||
        '';

      const externalCode =
        order.externalCode ||
        order.codiceEsterno ||
        summary.codiceEsterno ||
        summary.numeroIntervento ||
        '';

      const companyName =
        summary.cliente || order.companyName || order.cliente || '';

      const place =
        summary.luogo ||
        order.place ||
        order.luogo ||
        order.indirizzo ||
        '';

      const assignedUser = item.tecnicoId || order.assignedTo;

      const dataRicezione =
        order.dataRicezioneLavorazione ||
        order.dataCreazione ||
        order.createdAt ||
        null;

      const dataEseguita =
        summary.data ||
        item.generatedAt ||
        order.dataIntervento ||
        order.dateIntervento ||
        null;

      if (!textMatch(externalCode, filters.codiceEsterno)) return false;
      if (!textMatch(fullCode, filters.codiceInterno)) return false;
      if (!textMatch(order.type, filters.tipoLavorazione)) return false;
      if (!textMatch(companyName, filters.cliente)) return false;
      if (!textMatch(place, filters.luogo)) return false;

      if (filters.assegnatoA) {
        const label = formatUserLabel(assignedUser);
        if (!textMatch(label, filters.assegnatoA)) return false;
      }

      if (
        !inDateRange(
          dataRicezione,
          filters.dataRicezioneDa,
          filters.dataRicezioneA
        )
      ) {
        return false;
      }

      if (
        !inDateRange(
          dataEseguita,
          filters.dataEseguitaDa,
          filters.dataEseguitaA
        )
      ) {
        return false;
      }

      // 🔹 filtro stato ordine (solo per interventi)
      if (filters.statoOrdine) {
        const status = order.orderStatus || '';
        if (status !== filters.statoOrdine) return false;
      }

      return true;
    });
  }, [items, filters, viewType]);

  // ===============================
  // GRUPPO PER CODICE BASE
  // ===============================
  const groupedByBaseCode = useMemo(() => {
    const groupsMap = new Map();

    filteredItems.forEach((item) => {
      const order = item.workOrder || {};
      const summary = item.summary || {};

      const fullCode =
        summary.codiceInterno ||
        order.internalCode ||
        order.codiceInterno ||
        '';

      const baseCode = getBaseInternalCode(fullCode);

      const tecnicoName =
        item.tecnicoName ||
        (item.tecnicoId && formatUserLabel(item.tecnicoId)) ||
        '';

      const dataIntervento =
        summary.data ||
        order.dataIntervento ||
        order.dateIntervento ||
        item.generatedAt ||
        null;

      if (!groupsMap.has(baseCode)) {
        groupsMap.set(baseCode, {
          baseCode,
          order,
          items: [],
          lastGeneratedAt: item.generatedAt || null,
          lastTecnico: tecnicoName || '',
          lastDataIntervento: dataIntervento || null,
        });
      }

      const group = groupsMap.get(baseCode);

      group.items.push({
        ...item,
        _fullCode: fullCode || baseCode,
        _tecnicoName: tecnicoName,
        _dataIntervento: dataIntervento,
      });

      const currentLast = group.lastGeneratedAt
        ? new Date(group.lastGeneratedAt).getTime()
        : 0;
      const thisTime = item.generatedAt
        ? new Date(item.generatedAt).getTime()
        : 0;

      if (thisTime >= currentLast) {
        group.lastGeneratedAt = item.generatedAt || group.lastGeneratedAt;
        group.lastTecnico = tecnicoName || group.lastTecnico;
        group.lastDataIntervento = dataIntervento || group.lastDataIntervento;
      }
    });

    return Array.from(groupsMap.values()).sort((a, b) =>
      a.baseCode.localeCompare(b.baseCode)
    );
  }, [filteredItems]);

  // =======================
  // MODALE ELIMINAZIONE
  // =======================
  const openDeleteModalOrder = (order, baseCode) => {
    if (!order) return;
    setError('');
    setDeleteTarget({
      kind: 'order',
      id: order._id,
      displayCode: baseCode || order.internalCode || '',
    });
    setDeleteConfirmCode('');
  };

  const openDeleteModalWip = (wip) => {
    if (!wip) return;
    setError('');
    setDeleteTarget({
      kind: 'wip',
      id: wip._id,
      displayCode: wip._fullCode || '',
    });
    setDeleteConfirmCode('');
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteConfirmCode('');
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    const expected = (deleteTarget.displayCode || '').trim();
    const typed = (deleteConfirmCode || '').trim();

    if (!typed) {
      setError('Per eliminare devi inserire il codice interno esatto.');
      return;
    }
    if (typed !== expected) {
      setError('Codice interno non corretto, eliminazione annullata.');
      return;
    }

    setDeleteLoading(true);
    try {
      if (deleteTarget.kind === 'order') {
        const res = await authFetch(`${API_BASE}/work/${deleteTarget.id}`, {
          method: 'DELETE',
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(
            data?.error || 'Errore del server durante la cancellazione.'
          );
        }

        setItems((prev) =>
          prev.filter(
            (it) => String(it.workOrder?._id) !== String(deleteTarget.id)
          )
        );
      } else if (deleteTarget.kind === 'wip') {
        const res = await authFetch(
          `${API_BASE}/work/in-progress/${deleteTarget.id}`,
          {
            method: 'DELETE',
          }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(
            data?.error || 'Errore del server durante la cancellazione.'
          );
        }

        setItems((prev) => prev.filter((it) => it._id !== deleteTarget.id));
      }

      closeDeleteModal();
    } catch (err) {
      console.error('Errore handleDelete:', err);
      setError(err.message || 'Errore del server durante la cancellazione.');
      setDeleteLoading(false);
    }
  };

  // =======================
  // RENDER
  // =======================
  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-800">
              Lavorazioni terminate
            </h1>
            <p className="text-xs text-gray-500">
              Storico dei VIT e delle manutenzioni chiusi.
            </p>
          </div>

          <button
            onClick={() => navigate('/lavorazioni')}
            className="hidden md:inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </div>

        {/* Switch VIT / Manutenzioni */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setViewType('vit')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
              viewType === 'vit'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Verbali di intervento
          </button>
          <button
            type="button"
            onClick={() => setViewType('manutenzione')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
              viewType === 'manutenzione'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Manutenzioni
          </button>
        </div>

        {/* Filtri */}
        <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
            <input
              type="text"
              name="codiceEsterno"
              value={filters.codiceEsterno}
              onChange={handleFilterChange}
              placeholder="Codice esterno"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="text"
              name="codiceInterno"
              value={filters.codiceInterno}
              onChange={handleFilterChange}
              placeholder="Codice interno (es. I25-00000-2)"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="text"
              name="tipoLavorazione"
              value={filters.tipoLavorazione}
              onChange={handleFilterChange}
              placeholder="Tipo lavorazione"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="text"
              name="cliente"
              value={filters.cliente}
              onChange={handleFilterChange}
              placeholder="Azienda / Cliente"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="text"
              name="luogo"
              value={filters.luogo}
              onChange={handleFilterChange}
              placeholder="Luogo intervento"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
            <input
              type="text"
              name="assegnatoA"
              value={filters.assegnatoA}
              onChange={handleFilterChange}
              placeholder="Assegnato a"
              className="border rounded-lg px-3 py-1.5 text-xs"
            />

            {/* filtro stato ordine */}
            <select
              name="statoOrdine"
              value={filters.statoOrdine}
              onChange={handleFilterChange}
              className="border rounded-lg px-3 py-1.5 text-xs bg-white"
            >
              <option value="">Tutti gli stati ordine</option>
              <option value="Ordine ricevuto">Ordine ricevuto</option>
              <option value="Ordine non ricevuto">Ordine non ricevuto</option>
            </select>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500">
                Data ricezione da
              </label>
              <input
                type="date"
                name="dataRicezioneDa"
                value={filters.dataRicezioneDa}
                onChange={handleFilterChange}
                className="border rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500">
                Data ricezione a
              </label>
              <input
                type="date"
                name="dataRicezioneA"
                value={filters.dataRicezioneA}
                onChange={handleFilterChange}
                className="border rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500">
                Data eseguita da
              </label>
              <input
                type="date"
                name="dataEseguitaDa"
                value={filters.dataEseguitaDa}
                onChange={handleFilterChange}
                className="border rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-gray-500">
                Data eseguita a
              </label>
              <input
                type="date"
                name="dataEseguitaA"
                value={filters.dataEseguitaA}
                onChange={handleFilterChange}
                className="border rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-xs text-gray-500">Caricamento...</p>
        ) : groupedByBaseCode.length === 0 ? (
          <p className="text-xs text-gray-500">
            Nessuna lavorazione terminata trovata con i filtri attuali.
          </p>
        ) : (
          <div className="space-y-3">
            {groupedByBaseCode.map((group) => {
              const order = group.order || {};
              const summary = group.items[0]?.summary || {};
              const companyName =
                summary.cliente ||
                order.companyName ||
                order.cliente ||
                'Cliente non specificato';
              const place =
                summary.luogo ||
                order.place ||
                order.luogo ||
                order.indirizzo ||
                'Luogo non specificato';

              const tipoLabel =
                order.type === 'manutenzione'
                  ? 'Manutenzione'
                  : 'Verbale di intervento';

              return (
                <div
                  key={group.baseCode}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-3"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700">
                          {tipoLabel}
                        </span>
                        <span className="font-mono text-xs font-semibold text-blue-700">
                          {group.baseCode}
                        </span>
                      </div>

                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {companyName}
                      </p>
                      <p className="text-[11px] text-gray-600 truncate">
                        {place}
                      </p>

                      <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-gray-500">
                        <p>
                          <span className="font-semibold">Ultimo tecnico:</span>{' '}
                          {group.lastTecnico || '—'}
                        </p>
                        <p>
                          <span className="font-semibold">
                            Ultima data intervento:
                          </span>{' '}
                          {formatDate(group.lastDataIntervento)}
                        </p>
                        <p>
                          <span className="font-semibold">
                            N. interventi totali:
                          </span>{' '}
                          {group.items.length}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group.baseCode)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {expanded.includes(group.baseCode)
                            ? 'Nascondi dettagli'
                            : 'Mostra dettagli interventi'}
                        </button>

                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() =>
                              openDeleteModalOrder(group.order, group.baseCode)
                            }
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            Elimina lavorazione
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400">
                        Report ultimo giro generato il{' '}
                        {formatDate(group.lastGeneratedAt)}
                      </p>
                    </div>
                  </div>

                  {expanded.includes(group.baseCode) && (
                    <div className="mt-3 pt-3 border-t border-gray-200 overflow-x-auto">
                      <p className="text-[11px] text-gray-400 mb-2 md:hidden">
                        Scorri orizzontalmente la tabella per visualizzare tutti
                        i dati.
                      </p>
                      <table className="min-w-full text-[11px]">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-2 pr-3">Cod. interno</th>
                            <th className="pb-2 pr-3">Cod. esterno</th>
                            <th className="pb-2 pr-3">Tipo lav.</th>
                            <th className="pb-2 pr-3">Preventivo</th>
                            <th className="pb-2 pr-3">Consuntivo</th>
                            <th className="pb-2 pr-3">Materiale arrivato</th>
                            <th className="pb-2 pr-3">Azienda / Cliente</th>
                            <th className="pb-2 pr-3">Luogo</th>
                            <th className="pb-2 pr-3">Data ricezione</th>
                            <th className="pb-2 pr-3">Data intervento</th>
                            <th className="pb-2 pr-3">Tecnico</th>
                            <th className="pb-2 pr-3">Stato ordine</th>
                            <th className="pb-2 pr-3">Report generato</th>
                            <th className="pb-2 pr-3">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((it) => {
                            const o = it.workOrder || group.order || {};
                            const s = it.summary || {};

                            const externalCode =
                              o.externalCode ||
                              o.codiceEsterno ||
                              s.codiceEsterno ||
                              s.numeroIntervento ||
                              '';

                            const companyNameRow =
                              s.cliente ||
                              o.companyName ||
                              o.cliente ||
                              '—';

                            const placeRow =
                              s.luogo ||
                              o.place ||
                              o.luogo ||
                              o.indirizzo ||
                              '—';

                            const dataRicezioneRow =
                              o.dataRicezioneLavorazione ||
                              o.dataCreazione ||
                              o.createdAt ||
                              null;

                            const tipoLabelRow =
                              o.type === 'manutenzione'
                                ? 'Manutenzione'
                                : 'Intervento';

                            const orderStatusLabel =
                              o.type === 'intervento'
                                ? o.orderStatus || '-'
                                : '-';

                            const preventivoLabel =
                              o.type === 'intervento'
                                ? o.preventivoStatus || '-'
                                : '-';

                            const consuntivoLabel =
                              o.type === 'intervento' && o.preventivoStatus === 'A consuntivo'
                                ? o.consuntivoStatus || '-'
                                : '-';

                            const materialeArrivatoLabel =
                              o.type === 'intervento'
                                ? o.materialeArrivato || '-'
                                : '-';
                            return (
                              <tr
                                key={it._id}
                                className="border-t border-gray-100 align-top"
                              >
                                <td className="py-2 pr-3 whitespace-nowrap font-mono">
                                  {it._fullCode}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {externalCode || '—'}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {tipoLabelRow}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {preventivoLabel}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {consuntivoLabel}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {materialeArrivatoLabel}
                                </td>
                                <td className="py-2 pr-3">
                                  {companyNameRow}
                                </td>
                                <td className="py-2 pr-3">
                                  {placeRow}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {formatDate(dataRicezioneRow)}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {formatDate(it._dataIntervento)}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {it._tecnicoName || '—'}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                      orderStatusLabel === 'Ordine ricevuto'
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : orderStatusLabel ===
                                          'Ordine non ricevuto'
                                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                                    }`}
                                  >
                                    {orderStatusLabel}
                                  </span>
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {formatDate(it.generatedAt)}
                                </td>
                                <td className="py-2 pr-3">
                                  <div className="flex gap-2 whitespace-nowrap">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenReport(it)}
                                      className="px-3 py-1 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
                                    >
                                      Apri
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleReopen(it._id)}
                                      disabled={actionLoadingId === it._id}
                                      className={`px-3 py-1 rounded-lg border text-[11px] font-semibold ${
                                        actionLoadingId === it._id
                                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                          : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500 hover:text-emerald-700'
                                      }`}
                                    >
                                      {actionLoadingId === it._id
                                        ? 'Riapro...'
                                        : 'Riassegna'}
                                    </button>

                                    {isAdmin && (
                                      <button
                                        type="button"
                                        onClick={() => openDeleteModalWip(it)}
                                        className="px-3 py-1 rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 text-[11px] font-semibold"
                                      >
                                        Elimina
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="md:hidden flex justify-center">
          <button
            onClick={() => navigate('/lavorazioni')}
            className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </div>
      </div>

      {/* MODALE ELIMINAZIONE SOLO ADMIN */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="text-sm font-semibold text-red-700 mb-2">
              Conferma eliminazione
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              Stai per eliminare{' '}
              {deleteTarget.kind === 'order'
                ? 'tutta la lavorazione (tutti i giri) con codice interno '
                : 'questo singolo intervento con codice interno '}
              <span className="font-mono font-semibold">
                {deleteTarget.displayCode}
              </span>
              .
            </p>
            <p className="text-xs text-gray-600 mb-3">
              L&apos;operazione non è reversibile. Per confermare digita
              esattamente il codice interno.
            </p>
            <input
              type="text"
              className="w-full border rounded-lg px-3 py-1.5 text-sm mb-3"
              value={deleteConfirmCode}
              onChange={(e) => setDeleteConfirmCode(e.target.value)}
              placeholder={deleteTarget.displayCode}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                disabled={deleteLoading}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {deleteLoading ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LavorazioniTerminate;
